
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandMixin from "@/mixins/brandMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";

// Types
import { IProvider } from "@/models/providers";
import {
  IPaymentProvidersProvider,
  IPaymentProvidersProviderMethods
} from "@/models/providers/paymentProviders";
import { IState } from "@/store";
import { IBrandGateway, IGateway } from "@/models/gateways";

@Component({
  computed: {
    ...mapState({
      brandAssignedGatewaysScope(state: IState, getters) {
        return getters[`data/${DataModules.BRAND_GATEWAYS}/scope`](
          this.brandId
        );
      },
      brandAssignedGateways(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.BRAND_GATEWAYS,
          scope: this.brandAssignedGatewaysScope
        });
      },

      orgGatewaysScope(state: IState, getters) {
        return getters[`data/${DataModules.GATEWAYS}/scope`];
      },
      orgGateways(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.GATEWAYS,
          scope: this.orgGatewaysScope
        });
      }
    })
  }
})
export default class PaymentProvidersProvider
  extends Mixins(BrandMixin, RequestPromises)
  implements IProvider {
  // Map State

  public brandAssignedGateways!: IBrandGateway[];
  public brandAssignedGatewaysScope!: string;

  public orgGateways!: IGateway[];
  public orgGatewaysScope!: string;

  // Props

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: false })
  public declare handleRequestError: boolean;

  @Prop({ default: false })
  public declare handleGuarding: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Variables

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public hasRequestError = false;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.loadData();

    this.$bus.$on("gatewaysChanged", () => {
      this.refreshData();
    });
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.BRAND_GATEWAYS,
      scope: this.brandAssignedGatewaysScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.GATEWAYS,
      scope: this.orgGatewaysScope,
      vm: this
    });

    this.$bus.$off("gatewaysChanged");
  }

  // Methods

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await Promise.all([
        this.getOrgGateways({ ignoreStored }),
        this.getBrandAssignedGateways({ ignoreStored }),
        this.refreshBrandCurrencies({ ignoreStored })
      ]);

      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public refreshData() {
    this.isLoading = true;
    this.loadData({ ignoreStored: true });
  }

  public getOrgGateways({ ignoreStored } = { ignoreStored: false }) {
    return this.$store.dispatch(`data/${DataModules.GATEWAYS}/list`, {
      scope: this.orgGatewaysScope,
      params: {
        limit: 0,
        with:
          "payment_type,application_access_token,countries,gateway_provider.logos"
      },
      ignoreStored,
      vm: this
    });
  }

  public refreshBrandCurrencies({ ignoreStored } = { ignoreStored: false }) {
    if (!ignoreStored) return Promise.resolve();
    return this.$store.dispatch("brand/get", { ignoreStored });
  }

  public getBrandAssignedGateways({ ignoreStored } = { ignoreStored: false }) {
    return this.$store.dispatch(`data/${DataModules.BRAND_GATEWAYS}/list`, {
      brandId: this.brandId,
      scope: this.brandAssignedGatewaysScope,
      params: {
        limit: 0,
        with:
          "gateway.payment_type,gateway.application_access_token,gateway.countries,gateway.gateway_provider.logos"
      },
      ignoreStored,
      vm: this
    });
  }

  public managePaymentProvider(gateway: IGateway) {
    if (!this.canEdit) return;

    this.$store.dispatch(`data/${DataModules.GATEWAYS}/openManageGateway`, {
      gateway,
      gatewayProviderId: gateway.gateway_provider_id,
      actionType: ActionTypes.UPDATE
    });
  }

  public createPaymentProvider() {
    if (!this.canCreate) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.GATEWAYS}/openManageGateway`,
      {
        total: this.orgGateways.length,
        actionType: ActionTypes.CREATE
      }
    );
  }

  public assignPaymentProvider(gatewayId: IGateway["id"]) {
    if (!this.canEdit) return Promise.resolve();

    return this.updatePaymentProvider(gatewayId, {
      active: true,
      brand_id: this.brandId,
      gateway_id: gatewayId,
      order: this.brandAssignedGateways.length,
      visible: true
    });
  }

  public updatePaymentProvider(gatewayId: IGateway["id"], payload: object) {
    return this.$store.dispatch(
      `data/${DataModules.GATEWAYS}/changeBrandGateway`,
      {
        gatewayId,
        data: {
          brand_id: this.brandId,
          ...payload
        }
      }
    );
  }

  public reorder(newOrder: number[]) {
    return this.$store.dispatch(`data/${DataModules.GATEWAYS}/reorder`, {
      brandId: this.brandId,
      data: {
        gateway_ids: newOrder
      }
    });
  }

  public async deletePaymentProvider(paymentProvider: any) {
    if (!this.canDelete) return;

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_payment_provider_confirmation")
        },
        events: {
          confirmed: async () => {
            try {
              if (paymentProvider.active) {
                await this.updatePaymentProvider(paymentProvider.id, {
                  active: false,
                  gateway_id: paymentProvider.id
                });
              }

              await this.deleteGateway(paymentProvider.id);

              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_sentence.payment_provider_successfully_removed"
                )
              });

              this.refreshData();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              confirm.close();
            }
          }
        }
      }
    });
  }

  public deleteGateway(gatewayId: IGateway["id"]) {
    return this.$store.dispatch(`data/${DataModules.GATEWAYS}/remove`, {
      id: gatewayId,
      vm: this
    });
  }

  public async updatePaymentProviderData(
    gatewayId: IGateway["id"],
    paymentData: object
  ) {
    try {
      this.isProcessing = true;
      await this.updatePaymentProvider(gatewayId, paymentData);
      await this.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Computed

  public get assignedPaymentProvider() {
    return _.orderBy(
      _.map(this.brandAssignedGateways, provider => {
        return {
          ...provider,
          ...provider.gateway,
          gateway_provider: provider.gateway.gateway_provider
        };
      }),
      ["order", "name"],
      ["asc", "asc"]
    );
  }

  public get unassignedPaymentProviders() {
    return _.orderBy(
      _.filter(this.orgGateways, orgGateway => {
        return !_.some(this.brandAssignedGateways, [
          "gateway_id",
          orgGateway.id
        ]);
      }),
      ["order", "name"],
      ["asc", "asc"]
    );
  }

  public get showUnassignedPaymentProviders() {
    return this.$store.getters["org/hasMultibrandEnabled"];
  }

  // Guarding

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canCreate() {
    return this.$userCan("create_payment_gateway") && this.contentIsAvailable;
  }

  public get canEdit() {
    return this.$userCan("update_payment_gateway");
  }

  public get canDelete() {
    return this.$userCan("delete_payment_gateway");
  }

  public get canReorder() {
    return this.$userCan("reorder_brand_payment_gateway");
  }

  public get guardFunctionalities() {
    return ["list_brand_gateways"];
  }

  // Provide

  @Provide("paymentProvidersProvider")
  public providerData(): IPaymentProvidersProvider {
    return {
      canCreate: this.canCreate,
      canDelete: this.canDelete,
      canEdit: this.canEdit,
      canReorder: this.canReorder,
      guardFunctionalities: this.guardFunctionalities,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      paymentProviders: this.assignedPaymentProvider,
      showUnassignedPaymentProviders: this.showUnassignedPaymentProviders,
      unassignedPaymentProviders: this.unassignedPaymentProviders
    };
  }

  @Provide("paymentProvidersProviderMethods")
  public get providerMethods(): IPaymentProvidersProviderMethods {
    return {
      assignPaymentProvider: this.assignPaymentProvider,
      createPaymentProvider: this.createPaymentProvider,
      deletePaymentProvider: this.deletePaymentProvider,
      managePaymentProvider: this.managePaymentProvider,
      refreshData: this.refreshData,
      reloadData: this.reloadData,
      reorder: this.reorder,
      updatePaymentProviderData: this.updatePaymentProviderData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $paymentProvidersData: this.providerData(),
        $paymentProvidersMethods: this.providerMethods
      })
    );
  }
}
